import React, { useEffect, useContext, useState } from "react";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import EmailIcon from "@mui/icons-material/Email";
import { makeStyles } from "@mui/styles";
import { motion, useAnimation } from "framer-motion";
import IconBtn from "../components/IconBtn";
import LoaderContext from "../components/LoaderContext";
import SvgIcon from "@mui/material/SvgIcon";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    position: "fixed",
    bottom: 0,
    right: 0,
    padding: theme.spacing(2),
    zIndex: 100,
  },
  icon: {
    color: "#FFF",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
    fontSize: (props) => (props.fontSize ? `${props.fontSize}px` : "32px"),
    position: "relative",
    "&:hover": {
      color: "#ffb510",
      transform: "scale(1.1)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      width: "0",
      height: "0",
      background:
        "radial-gradient(circle, rgba(255, 181, 16, 0.3) 0%, transparent 70%)",
      borderRadius: "50%",
      transform: "translate(-50%, -50%)",
      transition: "all 0.3s ease",
      zIndex: -1,
    },
    "&:hover::before": {
      width: "60px",
      height: "60px",
    },
  },
  mobileWrapper: {
    display: "flex",
  },
  socialContainer: {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "-10px",
      right: "-10px",
      bottom: "-10px",
      left: "-10px",
      background:
        "linear-gradient(45deg, rgba(255, 181, 16, 0.1), rgba(255, 107, 53, 0.1))",
      borderRadius: "20px",
      opacity: 0,
      transition: "opacity 0.3s ease",
      zIndex: -1,
    },
    "&:hover::before": {
      opacity: 1,
    },
  },
}));

function Social() {
  const controls = useAnimation();
  const classes = useStyles();
  const { isLoading } = useContext(LoaderContext);
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!isLoading) {
      controls.start((i) => ({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          delay: 1.8 + i * 0.1,
          duration: 0.6,
          type: "spring",
          stiffness: 200,
        },
      }));
    } else {
      controls.start({ opacity: 0, y: 0, scale: 0.8 });
    }
  }, [isLoading, controls]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const socialLinks = [
    {
      icon: GitHubIcon,
      href: "https://github.com/humailsaleem123",
      label: "GitHub",
      color: "#333",
      delay: 0,
    },
    {
      icon: () => (
        <SvgIcon fontSize="small" style={{ width: "44px", height: "30px" }}>
          <path d="M15 21h-10v-2h10v2zm6-11.665l-1.621-9.335-1.993.346 1.62 9.335 1.994-.346zm-5.964 6.937l-9.746-.975-.186 2.016 9.755.879.177-1.92zm.538-2.587l-9.276-2.608-.526 1.954 9.306 2.5.496-1.846zm1.204-2.413l-8.297-4.864-1.029 1.743 8.298 4.865 1.028-1.744zm1.866-1.467l-5.339-7.829-1.672 1.14 5.339 7.829 1.672-1.14zm-2.644 4.195v8h-12v-8h-2v10h16v-10h-2z" />
        </SvgIcon>
      ),
      href: "https://stackoverflow.com/users/19606711/humail-saleem?tab=profile",
      label: "Stack Overflow",
      color: "#f48024",
      delay: 1,
    },
    {
      icon: LinkedInIcon,
      href: "https://www.linkedin.com/in/humail-saleem/",
      label: "LinkedIn",
      color: "#0077b5",
      delay: 2,
    },
    {
      icon: EmailIcon,
      href: "mailto:m.humailsaleem@gmail.com",
      label: "Email",
      color: "#ea4335",
      delay: 3,
    },
  ];

  return (
    <>
      <motion.div
        className={classes.wrapper}
        style={{
          "--mouse-x": `${mousePosition.x}px`,
          "--mouse-y": `${mousePosition.y}px`,
        }}
      >
        <motion.div
          className={classes.socialContainer}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          {socialLinks.map((social, index) => (
            <motion.div
              key={index}
              animate={controls}
              custom={index}
              initial={{ opacity: 0, y: 20, scale: 0.8 }}
              whileHover={{
                scale: 1.2,
                y: -5,
                transition: { duration: 0.2, type: "spring", stiffness: 400 },
              }}
              whileTap={{ scale: 0.9 }}
              onHoverStart={() => setHoveredIcon(index)}
              onHoverEnd={() => setHoveredIcon(null)}
              style={{ marginBottom: "8px" }}
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 + social.delay * 0.1, duration: 0.6 }}
                whileHover={{
                  rotate: [0, -10, 10, -10, 0],
                  transition: { duration: 0.5 },
                }}
              >
                {social.label === "Stack Overflow" ? (
                  <motion.a
                    className={`${classes.icon} social-icon`}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: hoveredIcon === index ? "#ffb510" : "#fff",
                      transform:
                        hoveredIcon === index ? "scale(1.1)" : "scale(1)",
                    }}
                    whileHover={{
                      scale: 1.15,
                      transition: { duration: 0.2 },
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {social.icon()}
                  </motion.a>
                ) : (
                  <motion.div
                    whileHover={{
                      scale: 1.1,
                      transition: { duration: 0.2 },
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <IconBtn
                      icon={social.icon}
                      m={1}
                      href={social.href}
                      className="social-icon"
                      style={{
                        color: hoveredIcon === index ? "#ffb510" : "#fff",
                        transform:
                          hoveredIcon === index ? "scale(1.1)" : "scale(1)",
                      }}
                    />
                  </motion.div>
                )}
              </motion.div>

              {/* Tooltip */}
              {hoveredIcon === index && (
                <motion.div
                  initial={{ opacity: 0, x: 10, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 10, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    position: "absolute",
                    right: "60px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "linear-gradient(135deg, #1a1a2e, #16213e)",
                    color: "#fff",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                    border: "1px solid rgba(255, 181, 16, 0.3)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                    zIndex: 1000,
                  }}
                >
                  {social.label}
                  <div
                    style={{
                      position: "absolute",
                      right: "-6px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 0,
                      height: 0,
                      borderTop: "6px solid transparent",
                      borderBottom: "6px solid transparent",
                      borderLeft: "6px solid rgba(255, 181, 16, 0.3)",
                    }}
                  />
                </motion.div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </>
  );
}

export default Social;
